/**
 * Utility functions for handling token storage in iframe contexts
 * Supports localStorage, postMessage, and cookies as fallback
 */

const STORAGE_PREFIX = 'qbo_';
const IFRAME_STORAGE_KEY = 'qbo_tokens';

/**
 * Check if the app is running inside an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin iframe will throw error, which means we're in an iframe
    return true;
  }
};

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get tokens from localStorage
 */
const getTokensFromLocalStorage = (): {
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
} | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const tokensJson = localStorage.getItem(STORAGE_PREFIX + IFRAME_STORAGE_KEY);
    if (tokensJson) {
      return JSON.parse(tokensJson);
    }
  } catch (e) {
    console.error('Error reading from localStorage:', e);
  }
  return null;
};

/**
 * Save tokens to localStorage
 */
const setTokensToLocalStorage = (tokens: {
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
}): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(STORAGE_PREFIX + IFRAME_STORAGE_KEY, JSON.stringify(tokens));
  } catch (e) {
    console.error('Error writing to localStorage:', e);
  }
};

/**
 * Remove tokens from localStorage
 */
const removeTokensFromLocalStorage = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_PREFIX + IFRAME_STORAGE_KEY);
  } catch (e) {
    console.error('Error removing from localStorage:', e);
  }
};

/**
 * Request tokens from parent window via postMessage
 */
const requestTokensFromParent = (): Promise<{
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
} | null> => {
  return new Promise((resolve) => {
    if (!isInIframe()) {
      resolve(null);
      return;
    }

    const messageId = `qbo_token_request_${Date.now()}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(null);
    }, 1000); // 1 second timeout

    const handler = (event: MessageEvent) => {
      // Security: Verify origin if possible (you may want to add origin check)
      if (event.data?.type === 'qbo_tokens_response' && event.data?.requestId === messageId) {
        clearTimeout(timeout);
        window.removeEventListener('message', handler);
        resolve(event.data.tokens || null);
      }
    };

    window.addEventListener('message', handler);

    // Request tokens from parent
    try {
      window.parent.postMessage(
        {
          type: 'qbo_tokens_request',
          requestId: messageId,
        },
        '*', // In production, specify the exact origin
      );
    } catch (e) {
      clearTimeout(timeout);
      window.removeEventListener('message', handler);
      console.error('Error sending postMessage to parent:', e);
      resolve(null);
    }
  });
};

/**
 * Send tokens to parent window via postMessage
 */
const sendTokensToParent = (tokens: {
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
}): void => {
  if (!isInIframe()) {
    return;
  }

  try {
    window.parent.postMessage(
      {
        type: 'qbo_tokens_update',
        tokens,
      },
      '*', // In production, specify the exact origin
    );
  } catch (e) {
    console.error('Error sending tokens to parent:', e);
  }
};

/**
 * Get tokens with fallback strategy:
 * 1. Try localStorage (for same-origin iframes)
 * 2. Try postMessage to parent (for cross-origin iframes)
 * 3. Fallback to cookies (for non-iframe or when others fail)
 */
export const getTokensFromStorage = async (): Promise<{
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
} | null> => {
  // Try localStorage first (works in same-origin iframes)
  const localStorageTokens = getTokensFromLocalStorage();
  if (localStorageTokens && (localStorageTokens.qboIdToken || localStorageTokens.qboAccessToken)) {
    return localStorageTokens;
  }

  // If in iframe, try requesting from parent window
  if (isInIframe()) {
    const parentTokens = await requestTokensFromParent();
    if (parentTokens && (parentTokens.qboIdToken || parentTokens.qboAccessToken)) {
      // Cache in localStorage for future use
      setTokensToLocalStorage(parentTokens);
      return parentTokens;
    }
  }

  return null;
};

/**
 * Set tokens with multiple storage strategies
 */
export const setTokensToStorage = (tokens: {
  qboIdToken?: string;
  qboAccessToken?: string;
  qboRefreshToken?: string;
  qboLoginDate?: string;
}): void => {
  // Save to localStorage (works in same-origin iframes)
  setTokensToLocalStorage(tokens);

  // If in iframe, also send to parent window
  if (isInIframe()) {
    sendTokensToParent(tokens);
  }
};

/**
 * Remove tokens from all storage locations
 */
export const removeTokensFromStorage = (): void => {
  removeTokensFromLocalStorage();

  if (isInIframe()) {
    try {
      window.parent.postMessage(
        {
          type: 'qbo_tokens_remove',
        },
        '*', // In production, specify the exact origin
      );
    } catch (e) {
      console.error('Error sending remove message to parent:', e);
    }
  }
};

/**
 * Listen for token requests from child iframes (for parent window)
 */
export const setupParentWindowListener = (): (() => void) => {
  const handler = (event: MessageEvent) => {
    // Security: Verify origin if possible
    if (event.data?.type === 'qbo_tokens_request') {
      const tokens = getTokensFromLocalStorage();
      try {
        // Type guard to ensure event.source is a Window
        if (event.source && 'postMessage' in event.source) {
          // For Window objects, postMessage takes (message, targetOrigin)
          (event.source as Window).postMessage(
            {
              type: 'qbo_tokens_response',
              requestId: event.data.requestId,
              tokens,
            },
            '*', // In production, use event.origin
          );
        }
      } catch (e) {
        console.error('Error responding to token request:', e);
      }
    }
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
};
